/**
 * Ball in Court Table Component
 * Phase 6 Batch 3 - EOS Add-on Reports
 *
 * Action tracker showing what's waiting on us vs them (EOS L10 Meeting tool)
 */
import { useBallInCourt, type BallInCourtItem } from '@/hooks/useDashboardReports';
import { AlertCircle, Clock, AlertTriangle } from 'lucide-react';
import { useState } from 'react';

export function BallInCourtTable() {
  const { data, isLoading, error } = useBallInCourt();
  const [activeTab, setActiveTab] = useState<'us' | 'them'>('us');

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load ball in court data
      </div>
    );
  }

  const ballData = data as { success: boolean; waiting_on_us: BallInCourtItem[]; waiting_on_them: BallInCourtItem[]; summary: any };
  const waitingOnUs = ballData?.waiting_on_us || [];
  const waitingOnThem = ballData?.waiting_on_them || [];
  const summary = ballData?.summary || {
    waiting_on_us_count: 0,
    waiting_on_us_urgent: 0,
    waiting_on_them_count: 0,
    waiting_on_them_urgent: 0,
  };

  const currentItems = activeTab === 'us' ? waitingOnUs : waitingOnThem;

  const getUrgencyBadge = (urgency: string) => {
    switch (urgency) {
      case 'high':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
            <AlertTriangle size={12} />
            Urgent
          </span>
        );
      case 'medium':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded">
            <Clock size={12} />
            Medium
          </span>
        );
      case 'low':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-emerald-700 rounded">
            <Clock size={12} />
            Low
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="ball-in-court-table">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Ball in Court (Action Tracker)
        </h2>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div
          className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
            activeTab === 'us'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => setActiveTab('us')}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 mb-1">Waiting on Us</div>
              <div className="text-3xl font-bold text-sky-700">
                {summary.waiting_on_us_count}
              </div>
            </div>
            {summary.waiting_on_us_urgent > 0 && (
              <div className="text-right">
                <div className="text-xs text-gray-600">Urgent</div>
                <div className="text-2xl font-semibold text-red-600">
                  {summary.waiting_on_us_urgent}
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
            activeTab === 'them'
              ? 'border-violet-500 bg-violet-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => setActiveTab('them')}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 mb-1">Waiting on Them</div>
              <div className="text-3xl font-bold text-violet-700">
                {summary.waiting_on_them_count}
              </div>
            </div>
            {summary.waiting_on_them_urgent > 0 && (
              <div className="text-right">
                <div className="text-xs text-gray-600">Urgent</div>
                <div className="text-2xl font-semibold text-red-600">
                  {summary.waiting_on_them_urgent}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Project / Scope
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Organization
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status Notes
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Days Waiting
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                Urgency
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  {activeTab === 'us'
                    ? 'No items waiting on us'
                    : 'No items waiting on them'}
                </td>
              </tr>
            ) : (
              currentItems.map((item) => (
                <tr
                  key={item.scope_id}
                  className={`hover:bg-gray-50 ${
                    item.urgency === 'high' ? 'bg-red-50' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">
                      {item.project_number ? `P-${item.project_number}` : 'Proposal'}: {item.project_name}
                    </div>
                    <div className="text-xs text-gray-500">{item.scope_name}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {item.organization_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <div className="max-w-xs truncate">
                      {item.status_notes || 'No notes'}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      Updated {formatDate(item.status_updated_at)}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <span
                      className={`text-sm font-medium ${
                        item.days_waiting > 7
                          ? 'text-red-600'
                          : item.days_waiting > 3
                          ? 'text-yellow-600'
                          : 'text-gray-600'
                      }`}
                    >
                      {item.days_waiting} days
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    {getUrgencyBadge(item.urgency)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Help Text */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
        <strong>Ball in Court:</strong> Track action items by who needs to take the next step.
        Items turn urgent after 7+ days waiting. Use this in your weekly L10 meetings to keep
        projects moving forward.
      </div>
    </div>
  );
}
